import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import './styles.css';
import { useTranslation } from 'react-i18next';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface CommandSuggestion {
  command: string;
  displayName: string;
  description: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = "搜索笔记内容... (输入 / 进入命令模式)"
}) => {
  const { t } = useTranslation('searchbar');
  const [isCommandMode, setIsCommandMode] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [showCommandSuggestions, setShowCommandSuggestions] = useState(false);
  const [showCommandDropdown, setShowCommandDropdown] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<string>("search");
  const searchBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // 定义可用的命令
  const commandSuggestions: CommandSuggestion[] = [
    { 
      command: 'search', 
      displayName: t('commands.search_name', 'Search'), 
      description: t('commands.search_description', '搜索笔记内容') 
    },
    { 
      command: 'ask', 
      displayName: t('commands.ask_name', 'Ask'), 
      description: t('commands.ask_description', '向AI助手提问') 
    },
    { 
      command: 'parse', 
      displayName: t('commands.parse_name', 'Parse'), 
      description: t('commands.parse_description', '解析微信文章链接') 
    }
  ];

  // 获取当前选中命令的显示名称
  const getCommandDisplayName = (cmdName: string): string => {
    const cmd = commandSuggestions.find(c => c.command === cmdName);
    return cmd ? cmd.displayName : cmdName;
  };

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setTempValue(newValue);
    
    if (newValue.startsWith('/')) {
      setIsCommandMode(true);
      setShowCommandSuggestions(true);
      setSelectedIndex(-1);
    } else if (selectedCommand && selectedCommand !== 'search') {
      setIsCommandMode(true);
    } else {
      setIsCommandMode(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showCommandSuggestions) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < commandSuggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleCommandSelect(commandSuggestions[selectedIndex].command);
          } else if (isCommandMode) {
            handleSubmit();
          }
          break;
        case 'Escape':
          setShowCommandSuggestions(false);
          setShowCommandDropdown(false);
          setSelectedIndex(-1);
          break;
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCommandSelect = (command: string) => {
    setSelectedCommand(command);
    setTempValue('');
    setShowCommandSuggestions(false);
    setShowCommandDropdown(false);
    setIsCommandMode(command !== 'search');

    setTimeout(() => {
      const input = searchBarRef.current?.querySelector('input');
      if (input) {
        input.focus();
      }
    }, 0);
  };

  const isValidUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  };

  const extractUrl = (text: string): string | null => {
    const urlRegex = /(https?:\/\/[^\s]+)/;
    const match = text.match(urlRegex);
    return match ? match[0] : null;
  };

  const handleSubmit = () => {
    if (selectedCommand === 'search') {
      onChange(tempValue);
    } else if (selectedCommand === 'parse' && tempValue) {
      // 尝试从输入文本中提取URL
      const extractedUrl = extractUrl(tempValue);
      
      if (extractedUrl) {
        // 如果成功提取到URL，使用它
        onChange(`/parse ${extractedUrl}`);
      } else {
        // 如果没有找到URL，尝试原来的方法
        let url = tempValue;
        
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        
        if (isValidUrl(url)) {
          onChange(`/parse ${url}`);
        } else {
          console.error('Invalid URL format');
        }
      }
    } else if (selectedCommand === 'ask' && tempValue) {
      onChange(`/ask ${tempValue}`);
    } else if (tempValue) {
      onChange(tempValue);
    }
  };

  const handleClear = () => {
    setTempValue('');
    onChange('');
    setIsCommandMode(false);
    setShowCommandSuggestions(false);
    setSelectedCommand('search');
  };

  const toggleCommandDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCommandDropdown(prev => !prev);
    setShowCommandSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) &&
        (dropdownRef.current && !dropdownRef.current.contains(event.target as Node))
      ) {
        setShowCommandSuggestions(false);
        setShowCommandDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`search-bar-container ${isCommandMode ? 'command-mode' : ''}`} ref={searchBarRef}>
      <div 
        className="command-dropdown-button" 
        onClick={(e) => toggleCommandDropdown(e)}
        role="button"
        tabIndex={0}
      >
        {getCommandDisplayName(selectedCommand)}
      </div>
      
      {showCommandDropdown && (
        <div className="command-dropdown" ref={dropdownRef}>
          {commandSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className="command-dropdown-item"
              onClick={() => handleCommandSelect(suggestion.command)}
            >
              <span className="dropdown-command">{suggestion.displayName}</span>
              <span className="dropdown-description">{suggestion.description}</span>
            </div>
          ))}
        </div>
      )}
      
      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder={
            selectedCommand === 'parse' 
              ? t('searchbar.input_wechat_url')
              : selectedCommand === 'search'
              ? t('searchbar.input_search')
              : selectedCommand === 'ask'
              ? t('searchbar.input_ask')
              : t('searchbar.placeholder')
          }
          value={tempValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <button 
          className="submit-button"
          onClick={handleSubmit}
          title={t('searchbar.submit')}
        >
          <Search size={18} />
        </button>
        {value && (
          <button 
            className="clear-button"
            onClick={handleClear}
            title={t('searchbar.clear')}
          >
            ×
          </button>
        )}
      </div>
      {showCommandSuggestions && (
        <div className="command-suggestions">
          {commandSuggestions.map((suggestion, index) => (
            <div
              key={index}
              className={`command-suggestion-item ${index === selectedIndex ? 'active' : ''}`}
              onClick={() => handleCommandSelect(suggestion.command)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="command">/{suggestion.command}</span>
              <span className="description">{suggestion.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar; 